/**
 * 四种语言（PRD §8.4）：韩语、日语、英语、中文（简体）。
 */
export const LANGS = ['ko', 'ja', 'en', 'zh'] as const;
export type Lang = (typeof LANGS)[number];

/** 缺译兜底的固定后两级（PRD §8.4：用户语言 → 英语 → 中文）。 */
export const FALLBACK_LANGS: readonly Lang[] = ['en', 'zh'];

/** 识别不到时的最终语言（PRD §8.4：… → 英语）。 */
export const DEFAULT_LANG: Lang = 'en';

/** 词条源文语言：A45 每个词条都有中文源文（spec-core §9.8 `vals ?& ARRAY['zh']`）。 */
export const SOURCE_LANG: Lang = 'zh';

/**
 * 缺译兜底顺序的一项（参数 `content.i18n_fallback_order`，spec-core §1.9 I4、§6.16 #199）：
 * `user` 表示用户语言，其余为固定语言；首项固定 `user`。
 */
export type FallbackItem = 'user' | Lang;

/** 缺译兜底顺序默认值：用户语言 → 英语 → 中文（PRD 5.14.4、§8.4）。 */
export const DEFAULT_FALLBACK_ORDER: readonly FallbackItem[] = ['user', 'en', 'zh'];

/** 传给 Intl 的 BCP 47 标签。 */
export const LOCALE_TAGS: Readonly<Record<Lang, string>> = {
  ko: 'ko-KR',
  ja: 'ja-JP',
  en: 'en-US',
  zh: 'zh-CN',
};

export function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && (LANGS as readonly string[]).includes(value);
}

/** 把任意语言标签（ko、ko-KR、zh_Hans_CN、EN-us…）归一到四种语言之一；匹配不到返回 null。 */
export function normalizeLang(tag: string | null | undefined): Lang | null {
  if (!tag) return null;
  const primary = tag.trim().toLowerCase().split(/[-_]/)[0];
  return isLang(primary) ? primary : null;
}

/**
 * 按兜底顺序展开成语言链，去重。
 * - 首项总是用户语言（参数规定首项固定 user；传入的顺序不以 user 开头时也先查用户语言）；
 * - 链尾补上中文源文（每个词条都有中文，保证永不显示词条编号，spec-core §1.9 I4）。
 */
export function resolveFallbackChain(lang: Lang, order: readonly FallbackItem[] = DEFAULT_FALLBACK_ORDER): Lang[] {
  const chain: Lang[] = [lang];
  for (const item of order) {
    const next = item === 'user' ? lang : item;
    if (isLang(next) && !chain.includes(next)) chain.push(next);
  }
  if (!chain.includes(SOURCE_LANG)) chain.push(SOURCE_LANG);
  return chain;
}

/** 缺译兜底链：用户语言 → 英语 → 中文，去重。 */
export function fallbackChain(lang: Lang): Lang[] {
  return resolveFallbackChain(lang, DEFAULT_FALLBACK_ORDER);
}

/** 把参数值（list<enum(user, en, zh, ko, ja)>）规整为兜底顺序；不合法时返回默认值。 */
export function normalizeFallbackOrder(value: unknown): FallbackItem[] {
  if (!Array.isArray(value)) return [...DEFAULT_FALLBACK_ORDER];
  const out: FallbackItem[] = [];
  for (const item of value) {
    if ((item === 'user' || isLang(item)) && !out.includes(item)) out.push(item);
  }
  if (out.length === 0) return [...DEFAULT_FALLBACK_ORDER];
  // 首项固定 user（§6.16 #199"顺序可调（首项固定 user）"）
  return ['user', ...out.filter((item) => item !== 'user')];
}
